import React, {
  useState,
} from "react";
import {
  api,
  ApiClientError,
} from "../../api";
import { useToast } from "../../context/ToastContext";
import { formatDateTime } from "../../lib/format";
import type {
  ClinicalCareProgramDetailResponse,
  Id,
} from "../../types";
import {
  getAnatomicalTemplate,
} from "./anatomicalTemplates";
import {
  PremiumAnatomicalCanvas,
  PremiumTreatmentSummary,
} from "./PremiumAnatomicalCanvas";
import {
  administrationTraceLabel,
  findAdministration,
} from "./anatomicalTraceability";

export function SavedAnatomicalMaps({
  detail,
  onChanged,
}: {
  detail: ClinicalCareProgramDetailResponse;
  onChanged: () => void | Promise<void>;
}) {
  const { show } = useToast();
  const [signingId, setSigningId] =
    useState<Id | null>(null);

  async function signMap(
    mapId: Id
  ) {
    setSigningId(mapId);

    try {
      await api.signClinicalAnatomicalMap(
        detail.program.id,
        mapId
      );

      show(
        "Mapa anatómico firmado.",
        "success"
      );

      await onChanged();
    } catch (error) {
      show(
        error instanceof ApiClientError
          ? error.message
          : "No se pudo firmar el mapa anatómico.",
        "error"
      );
    } finally {
      setSigningId(null);
    }
  }

  return (
    <div className="premium-saved-maps">
      <div>
        <h3
          style={{
            margin: 0,
            fontSize: 16,
          }}
        >
          Mapas registrados
        </h3>
        <div className="meta">
          Borradores y documentos
          clínicos firmados del proceso.
        </div>
      </div>

      {detail.anatomical_maps.length === 0 ? (
        <div className="chart-empty">
          Todavía no se registró un mapa
          anatómico para este proceso.
        </div>
      ) : (
        <div className="premium-saved-map-grid">
          {detail.anatomical_maps.map(
            (anatomicalMap) => {
              const template =
                getAnatomicalTemplate(
                  anatomicalMap.template_code
                );

              return (
                <article
                  className="premium-saved-map-card"
                  key={anatomicalMap.id}
                >
                  <div className="chart-list-actions">
                    <div>
                      <strong>
                        {template.label}
                      </strong>
                      <div className="meta">
                        {
                          anatomicalMap.purpose
                        }{" "}
                        ·{" "}
                        {
                          anatomicalMap.status
                        }{" "}
                        ·{" "}
                        {
                          anatomicalMap.markers
                            .length
                        }{" "}
                        marcas
                      </div>
                    </div>

                    {anatomicalMap.status !==
                      "SIGNED" && (
                      <button
                        className="btn-primary"
                        disabled={
                          signingId ===
                          anatomicalMap.id
                        }
                        onClick={() =>
                          signMap(
                            anatomicalMap.id
                          )
                        }
                      >
                        {signingId ===
                        anatomicalMap.id
                          ? "Firmando…"
                          : "Firmar mapa"}
                      </button>
                    )}
                  </div>

                  <div className="premium-saved-map-canvas">
                    <PremiumAnatomicalCanvas
                      template={template}
                      markers={
                        anatomicalMap.markers
                      }
                    />
                  </div>

                  <PremiumTreatmentSummary
                    markers={
                      anatomicalMap.markers
                    }
                  />

                  <div className="premium-saved-map-markers">
                    {anatomicalMap.markers.map(
                      (marker) => (
                        <div
                          className="premium-saved-marker"
                          key={marker.id}
                        >
                          <span
                            className={
                              "premium-marker-dot " +
                              `premium-marker-${marker.marker_type.toLowerCase()}`
                            }
                          />
                          <div>
                            <strong>
                              {marker.label ||
                                marker.zone_code ||
                                marker.marker_key}
                            </strong>
                            {marker.administration_id && (
                              <div className="premium-administration-trace-inline">
                                {administrationTraceLabel(
                                  findAdministration(detail.administrations, marker.administration_id)
                                )}
                              </div>
                            )}
                            <div className="meta">
                              {
                                marker.marker_type
                              }{" "}
                              ·{" "}
                              {
                                marker.geometry_type
                              }
                              {marker.zone_code
                                ? ` · ${marker.zone_code}`
                                : ""}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {anatomicalMap.notes && (
                    <p>
                      {anatomicalMap.notes}
                    </p>
                  )}

                  <div className="meta">
                    Creado el{" "}
                    {formatDateTime(
                      anatomicalMap.created_at
                    )}
                  </div>

                  {anatomicalMap.status ===
                    "SIGNED" &&
                    anatomicalMap.signed_at && (
                      <div className="meta">
                        Firmado el{" "}
                        {formatDateTime(
                          anatomicalMap.signed_at
                        )}
                      </div>
                    )}
                </article>
              );
            }
          )}
        </div>
      )}
    </div>
  );
}
